import React from 'react'
import { graphql, useLazyLoadQuery } from 'react-relay'
import { useParams } from 'react-router-dom'
import { YStack, XStack } from 'tamagui'

import { EventCard } from '@newComponents/Event/EventCard'
import { PresentationCard } from '@newComponents/Presentation/PresentationCard'
import { GuildCard } from '@newComponents/Guild/GuildCard'
import { Text } from '@newComponents/Text'
import { Card } from '@newComponents/Card'
import { FormattedMarkdown } from '@newComponents/FormattedMarkdown/FormattedMarkdown'
import { ExploreMoreLink } from '../HomeScreen/ExploreMoreLink'

import type { UserBentoScreenQuery } from './__generated__/UserBentoScreenQuery.graphql'

const SectionHeader = ({
  title,
  href,
}: {
  title: string
  href?: string
}) => (
  <XStack justifyContent="space-between" alignItems="center">
    <Text fontWeight="$7" size="$7">
      {title}
    </Text>
    {href && <ExploreMoreLink href={href}>More</ExploreMoreLink>}
  </XStack>
)

export const UserBentoScreen = () => {
  const params = useParams<'actorId'>()

  const data = useLazyLoadQuery<UserBentoScreenQuery>(
    graphql`
      query UserBentoScreenQuery($id: String!) {
        nodeBySlugId(id: $id) {
          __typename

          ... on User {
            id
            slugId
            bio

            upcomingEvents: eventAttendance(
              first: 4
              timeSelection: UPCOMING
            ) {
              edges {
                node {
                  id
                  ...EventCardNew_event @arguments(includeAttendRow: true)
                }
              }
            }

            recentPresentations: presentationsByPresenterId(
              first: 4
              orderBy: [CREATED_AT_DESC]
            ) {
              edges {
                node {
                  id
                  ...PresentationCardNew_presentation
                }
              }
            }

            guildMemberships(
              first: 6
              orderBy: [MEMBERSHIP_LEVEL_ASC]
            ) {
              edges {
                node {
                  id
                  guild {
                    ...GuildCardNew_guild
                  }
                }
              }
            }

            pastEvents: eventAttendance(
              first: 3
              timeSelection: PAST
            ) {
              edges {
                node {
                  id
                  ...EventCardNew_event
                }
              }
            }
          }
        }

        ...EventCardNew_query
      }
    `,
    { id: params.actorId || '' }
  )

  if (data.nodeBySlugId?.__typename !== 'User') {
    return null
  }

  const user = data.nodeBySlugId
  const upcomingEvents = user.upcomingEvents.edges
  const presentations = user.recentPresentations.edges
  const guilds = user.guildMemberships.edges
  const pastEvents = user.pastEvents.edges

  const hasUpcoming = upcomingEvents.length > 0
  const hasPresentations = presentations.length > 0
  const hasGuilds = guilds.length > 0
  const hasPastEvents = pastEvents.length > 0

  return (
    <YStack gap="$8">
      {/* Upcoming Events */}
      {hasUpcoming && (
        <YStack gap="$5">
          <SectionHeader
            title="Upcoming Events"
            href={`/${user.slugId}/events`}
          />

          <XStack gap="$4" flexWrap="wrap">
            {upcomingEvents.map(({ node }) => (
              <YStack key={node.id} flex={1} flexBasis="45%" minWidth={240}>
                <EventCard
                  event={node}
                  query={data}
                  showAttendRow
                />
              </YStack>
            ))}
          </XStack>
        </YStack>
      )}

      {/* Presentations */}
      {hasPresentations && (
        <YStack gap="$5">
          <SectionHeader
            title="Presentations"
            href={`/${user.slugId}/presentations`}
          />

          <XStack gap="$4" flexWrap="wrap">
            {presentations.map(({ node }) => (
              <YStack key={node.id} flex={1} flexBasis="45%" minWidth={240}>
                <PresentationCard presentation={node} />
              </YStack>
            ))}
          </XStack>
        </YStack>
      )}

      {/* Guilds */}
      {hasGuilds && (
        <YStack gap="$5">
          <SectionHeader
            title="Guilds"
            href={`/${user.slugId}/guilds`}
          />

          <XStack gap="$4" flexWrap="wrap">
            {guilds.map(({ node }) => {
              if (!node?.guild) return null
              return (
                <YStack key={node.id} flex={1} flexBasis="45%" minWidth={240}>
                  <GuildCard guild={node.guild} expand />
                </YStack>
              )
            })}
          </XStack>
        </YStack>
      )}

      {/* About */}
      {user.bio && (
        <YStack gap="$4">
          <SectionHeader title="About" />
          <Card glass padding="$5">
            <FormattedMarkdown>{user.bio}</FormattedMarkdown>
          </Card>
        </YStack>
      )}

      {/* Past Events — shown only when no upcoming events */}
      {!hasUpcoming && hasPastEvents && (
        <YStack gap="$5">
          <SectionHeader
            title="Recent Events"
            href={`/${user.slugId}/events`}
          />

          <XStack gap="$4" flexWrap="wrap">
            {pastEvents.map(({ node }) => (
              <YStack key={node.id} flex={1} flexBasis="30%" minWidth={200}>
                <EventCard event={node} />
              </YStack>
            ))}
          </XStack>
        </YStack>
      )}
    </YStack>
  )
}

export default UserBentoScreen
